import React, { type ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Panel,
  PanelContent,
  Link as CauldronLink,
  LoaderOverlay,
  Tag
} from '@deque/cauldron-react';
import PageTitle from '../../common/components/PageTitle';
import useWalkthrough from '../hooks/useWalkthrough';
import useDidUpdate from '../../common/hooks/useDidUpdate';
import { useAxeDevtoolsProAnalytics } from '../../common/contexts/analytics';
import { useEnterprises } from '../../common/contexts/enterprises';
import { useAuthContext } from '../../common/contexts/auth';
import styles from './Walkthrough.css';
import { ProductSlugs, listProductAccess } from '@deque/billing-utils';
import WalkthroughStepper from '../components/WalkthroughStepper';
import {
  CompleteIGT,
  FreeUserSaveTest,
  OpenDevTools,
  RunScan,
  SaveTest,
  WalkthroughCompleted
} from '../components/ProWalkthrough';
import { type AxePurchaseState } from '@deque/billing-service-client';
import { HubSpotProperties, PRODUCT_URLS } from '../../common/constants';
import { analyticsEvent } from '../../common/api-client';
import { useServerInfo } from '../../common/contexts/serverInfo';

const validPurchaseStates: ReadonlyArray<AxePurchaseState> = [
  'trialing',
  'paid'
];

const extensionName = 'axe DevTools';

const Walkthrough = (): ReactElement => {
  const { t } = useTranslation();
  const { activeEnterprise, loading: enterpriseLoading } = useEnterprises();
  const { user, billingUser, loading: authLoading } = useAuthContext();
  const analytics = useAxeDevtoolsProAnalytics();
  const { serverInfo } = useServerInfo();
  const history = useHistory();
  const wrapper = useRef<HTMLDivElement>(null);
  const {
    isMac,
    completedSteps,
    onNextClick,
    onPreviousClick,
    awaitingMessage,
    resetSteps
  } = useWalkthrough();
  const subscriptions =
    (activeEnterprise
      ? activeEnterprise.subscriptions
      : billingUser?.subscriptions) || [];
  const productAccess = listProductAccess(subscriptions);
  const isPro = validPurchaseStates.includes(
    productAccess[ProductSlugs.axeDevToolsExtension]
  );
  const token = user?.token;

  useDidUpdate(() => {
    wrapper.current?.focus();
  }, [completedSteps]);

  const onSkipClick = () => {
    analytics.walkthroughSkip({
      currentStep: completedSteps
    });

    if (token) {
      analyticsEvent(token, HubSpotProperties.AXE_SKIP_WALKTHROUGH_DATE);
    }

    history.push('/axe-devtools');
  };

  const lastStep = isPro ? 4 : 2;
  const panelContentProps = {
    isMac,
    awaitingMessage,
    completedSteps,
    onNextClick
  };
  const trialStartURL = new URL(location.origin);
  trialStartURL.pathname =
    PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].trial;

  return (
    <>
      <PageTitle
        title={t('{{ extensionName }} Browser Extension Walkthrough', {
          extensionName
        })}
      />
      <div className={styles.wrap}>
        <h1>
          {t('{{ extensionName }} Browser Extension Walkthrough', {
            extensionName
          })}
        </h1>
        <WalkthroughStepper
          completedSteps={completedSteps}
          steps={[
            t('Open axe Devtools'),
            t('Run a scan'),
            <div key="save-test">
              {t('Save test')}{' '}
              {!isPro && <Tag className={styles.proTag}>{t('PRO')}</Tag>}
            </div>,
            <div key="complete-igt">
              {t('Complete an IGT')}{' '}
              {!isPro && <Tag className={styles.proTag}>{t('PRO')}</Tag>}
            </div>
          ]}
        />

        {(authLoading || enterpriseLoading) && (
          <LoaderOverlay
            focusOnInitialRender
            label={t('Loading walkthrough...')}
          />
        )}

        <Panel className={styles.panelWrap} ref={wrapper} tabIndex={-1}>
          <OpenDevTools {...panelContentProps} />
          <RunScan {...panelContentProps} />
          {isPro ? (
            <SaveTest {...panelContentProps} />
          ) : (
            <FreeUserSaveTest {...panelContentProps} />
          )}
          <CompleteIGT {...panelContentProps} />
          <WalkthroughCompleted {...panelContentProps} />

          {completedSteps < lastStep && (
            <PanelContent>
              {completedSteps > 0 && (
                <Button onClick={onPreviousClick} variant="secondary">
                  {t('Previous')}
                </Button>
              )}
              <Button onClick={onNextClick} disabled={awaitingMessage}>
                {t('Next')}
              </Button>
            </PanelContent>
          )}

          {completedSteps === 4 && (
            <PanelContent>
              <CauldronLink
                href="/axe-devtools"
                variant="button"
                onClick={onNextClick}
              >
                <span>{t('Go to welcome page')}</span>
              </CauldronLink>
            </PanelContent>
          )}

          {!isPro && completedSteps === 2 && (
            <PanelContent>
              {serverInfo?.isSelfProvisioningEnabled && (
                <CauldronLink
                  variant="button"
                  href={`/signup?product=${
                    ProductSlugs.axeDevToolsExtension
                  }&utm_source=webapp_walkthrough&utm_medium=referral&utm_campaign=walkthrough_upgrade_for_free&redirect_uri=${encodeURIComponent(
                    trialStartURL.href
                  )}`}
                >
                  {t('Upgrade for Free')}
                </CauldronLink>
              )}
              <CauldronLink
                variant="button-secondary"
                href={PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].welcome}
              >
                {t('Go to the Welcome Page')}
              </CauldronLink>
            </PanelContent>
          )}
        </Panel>

        {completedSteps <= 3 && (
          <CauldronLink href="#" onClick={onSkipClick}>
            {t('Skip walkthrough')}
          </CauldronLink>
        )}

        {completedSteps === 4 && (
          <Button onClick={resetSteps} variant="link">
            {t('Restart walkthrough')}
          </Button>
        )}
      </div>
    </>
  );
};

export default Walkthrough;
